import { FC, useEffect, useState } from 'react';
import { Filter, FilterTypes, FilterValue } from 'src/api/types';
import cn from 'classnames';
import NextImage from 'src/shared-components/NextImage/NextImage';
import { FormattedMessage, useIntl } from 'react-intl';
import Checkbox from 'src/shared-components/Input/Checkbox/Checkbox';
import useGTAG, { GTAGFilterPrefix } from '../../../hooks/useGTAG';
import trns from '../trns';

const searchIcon = 'shared/header-search.svg';

interface Props {
  filter: Filter;
  handleFilterUpdate: (values: (string | number)[]) => void;
  handleSubmit?: () => void;
}

const FilterComponent: FC<Props> = ({ filter, handleFilterUpdate }) => {
  const { values } = filter;

  const intl = useIntl();

  const [showAll, setShowAll] = useState<boolean>(values?.length <= 4);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [filterValue, setFilterValue] = useState<string | null>(null);

  const { trackGtagEvent } = useGTAG();

  const handleValueTrigger = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: FilterValue
  ) => {
    if (event.target.checked) {
      const updatedValues = [...selectedValues, value.id];
      handleFilterUpdate(updatedValues);
      if (GTAGFilterPrefix[filter.id] === 'kids') {
        trackGtagEvent('filter_kids', updatedValues, 'option_name');
      }
      trackGtagEvent(
        `filter_${GTAGFilterPrefix[filter.id] || filter.id}_choose`,
        updatedValues,
        'option_name'
      );
    } else {
      const selectedValuesCopy = [...selectedValues];
      const valueIndex = selectedValues.findIndex((item) => item === value.id);
      selectedValuesCopy.splice(valueIndex, 1);
      handleFilterUpdate(selectedValuesCopy);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setFilterValue(value);
    trackGtagEvent('filter_company_search', value, 'search_request');
  };

  useEffect(() => {
    setSelectedValues((filter.selectedValues as string[]) || []);
  }, [filter.selectedValues]);

  const maxValues = 4;

  const filteredValues =
    filterValue && filter.type === FilterTypes.ValuesSearch
      ? values.filter((value) => value.id?.includes(filterValue))
      : values;
  const valuesToShow = showAll
    ? filteredValues
    : filteredValues.slice(0, maxValues);
  return (
    <div className={cn('flex flex-col items-start')}>
      {filter.type === FilterTypes.ValuesSearch && (
        <div className="relative w-full mb-6">
          <input
            className="outline-none w-full flex rounded-md items-center pr-7 pl-5 py-2 border border-astronautBlue italic fs-caption-reg text-silver"
            placeholder={intl.formatMessage(trns.searchInFilters, {
              name: filter.name
            })}
            value={filterValue}
            onChange={handleSearchChange}
          />
          <div className="absolute top-3.5 right-2">
            <NextImage
              alt="search"
              src={searchIcon}
              layout="fixed"
              height={17}
              width={18}
              loading="eager"
            />
          </div>
        </div>
      )}
      <ul className="flex flex-col space-y-2 w-full">
        {valuesToShow?.map((value) => {
          const isChecked: boolean = selectedValues?.includes(value.id);
          const elementKey = `${filter.id}_${value.id}`;
          return (
            <li className="flex w-full" key={elementKey}>
              <Checkbox
                inputId={elementKey}
                title={`${value.name} (${value.count})`}
                wrapperClassName="items-center"
                titleClassName="text-darkBlue"
                isChecked={isChecked}
                onChange={(event) => handleValueTrigger(event, value)}
              />
            </li>
          );
        })}
      </ul>
      {filteredValues?.length > maxValues && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="underline italic text-darkBlue fs-caption-reg mt-6 ml-7"
        >
          <FormattedMessage {...trns.openAllFilters} />
        </button>
      )}
      {showAll && filteredValues?.length > maxValues && (
        <button
          onClick={() => setShowAll(false)}
          className="underline italic text-darkBlue fs-caption-reg mt-6 ml-7"
        >
          <FormattedMessage {...trns.hideFilters} />
        </button>
      )}
      <div className="border-b border-gallery w-full mt-6" />
    </div>
  );
};

export default FilterComponent;
